import './ExceedSendFooter.pcss';
import React from 'react';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { ErrorIcon } from '../../../../../common/component-icons/ErrorIcon/ErrorIcon';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import { EnhancedOutgoingMessagesStatistic } from '@mm/state/model/ConnectedPageModel';
import { getMessage } from './helpers';
import { StoreState } from '@mm/state/store-interfaces';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import modalActions, {
  OpenMonthlySendsWarningModalParams,
} from '@mm/state/modules/modalsState/actions';
import { defaultProUnicornPlanSelector } from '@mm/state/selectors/subscriptionSelectors';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { PercentageBar } from '../../../../../common/components/PercentageBar/PercentageBar';
import { ExclamationCircleFilled } from '@ant-design/icons';

type OuterProps = {
  facebookPage: EnhancedBillingPage;
  messagesStatistic: EnhancedOutgoingMessagesStatistic;
  showPercentageBar?: boolean;
};

type InnerProps = RouterComponentProps &
  ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

type Props = OuterProps & InnerProps;

const ExceedSendFooter = (props: Props) => {
  const {
    messagesStatistic,
    defaultUnicornPlan,
    openBillingModal,
    facebookPage,
    openMonthlySendsWarningModal,
    showPercentageBar,
  } = props;

  const billingPeriod = facebookPage?.subscription?.billingPeriodLength;
  const customPeriodLength = facebookPage.subscription?.planIntervalLength;
  const period =
    billingPeriod === 'annually'
      ? 'yearly'
      : billingPeriod === 'monthly' && (customPeriodLength || 0) > 1
      ? `${customPeriodLength} months`
      : 'monthly';

  const message = getMessage(messagesStatistic, period);
  const planInfo = getPlanInfo(facebookPage);
  const buttonText = planInfo.isFreePlan ? 'Upgrade' : 'Learn More';

  const onClickUpgrade = async () => {
    if (planInfo.isFreePlan) {
      if (defaultUnicornPlan) {
        openBillingModal(facebookPage, defaultUnicornPlan);
      }
    } else {
      if (
        (planInfo.isLegacyTeamPlan || planInfo.isCustomTeam) &&
        (!facebookPage.softLimitReached || !facebookPage.hardLimitReached)
      ) {
        return;
      }
      openMonthlySendsWarningModal({ facebookPageOverride: facebookPage });
    }
  };

  return (
    <div className="exceed-send-footer">
      <div className="exceed-send-footer__left-section">
        <ExclamationCircleFilled className="exceed-send-footer__icon" />

        <div className="exceed-send-footer__text">{message}</div>
      </div>

      {showPercentageBar && (
        <PercentageBar
          cardLayout={true}
          total={messagesStatistic.outgoingMessageLimit}
          used={messagesStatistic.cappedCount}
          showTotalNumber={false}
          showUsedNumber={false}
          className="exceed-send-footer__percentage-wrapper"
          percentageBarClassName="exceed-send-footer__percentage-bar"
          showUsedNumberRightSide
        />
      )}

      <PrimaryButton
        className="exceed-send-footer__upgrade-button"
        onClick={onClickUpgrade}
      >
        {buttonText}
      </PrimaryButton>
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  defaultUnicornPlan: defaultProUnicornPlanSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  openBillingModal: (facebookPage: EnhancedBillingPage, plan: EnhancedPlan) =>
    dispatch(
      modalActions.openBillingModal({
        facebookPage,
        plan,
        subscription: facebookPage.subscription,
      }),
    ),
  openMonthlySendsWarningModal: (params: OpenMonthlySendsWarningModalParams) =>
    dispatch(modalActions.openMonthlySendsWarningModal(params)),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(ExceedSendFooter);
